import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Card, ErrorState, KpiCard, Skeleton } from '@liftango/ui-elements';
import { opsColors, opsFontFamily, opsFontSize, opsFontWeight, opsSpace } from '../../../../../theme/opsTokens';
import type { KpiTone } from '../../../../../services/simulation/runReport';
import type { ReportSource } from '../useRunReport';
import type { KpiView } from './kpis';
import { formatNumber, formatSignedNumber } from './reportFormat';

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

const KPI_COLUMNS = 5;
const METRICS_KPI_COUNT = 4;
const VALUE_SKELETON = { width: '60%', height: '28px' } as const;
const LINE_SKELETON = { width: '80%', height: '12px' } as const;
const DELTA_SKELETON = { width: '50%', height: '12px' } as const;

const TONE_COLOR: Readonly<Record<KpiTone, string>> = {
  good: opsColors.semantic.success.color,
  bad: opsColors.semantic.error.color,
  neutral: opsColors.neutral.inkSoft,
};

export interface KpiStripProps {
  readonly kpis: readonly KpiView[];
  readonly metricsStatus: LoadStatus;
  /** Activities and vehicles, which vehicles used is derived from; `derivedFailedSource` names the one that failed. */
  readonly derivedStatus: LoadStatus;
  readonly derivedFailedSource: ReportSource | null;
  readonly compareStatus: LoadStatus;
  readonly compareLabel: string | null;
  readonly onRetry: (source: ReportSource) => void;
}

export const KpiStrip = memo(function KpiStrip(props: KpiStripProps) {
  const { t } = useTranslation('simulation');
  const { kpis, metricsStatus, compareStatus, onRetry } = props;
  const metricsFailed = metricsStatus === 'error';

  return (
    <Box data-testid="sim-results.report.kpis" sx={{ display: 'grid', gap: opsSpace[3] }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${KPI_COLUMNS}, minmax(0, 1fr))`, gap: opsSpace[3] }}>
        {metricsFailed ? (
          <Card data-testid="sim-results.report.kpis.error" style={{ gridColumn: `span ${METRICS_KPI_COUNT}` }}>
            <ErrorState title={t('results.report.errors.metrics')} onRetry={() => onRetry('metrics')} retryLabel={t('results.report.retry')} />
          </Card>
        ) : null}
        {kpis
          .filter((item) => !(metricsFailed && item.source === 'metrics'))
          .map((item) => (
            <KpiTile key={item.id} item={item} {...props} />
          ))}
      </Box>
      {compareStatus === 'error' ? (
        <Card data-testid="sim-results.report.kpis.compare-error">
          <ErrorState title={t('results.report.errors.compareMetrics')} onRetry={() => onRetry('compareMetrics')} retryLabel={t('results.report.retry')} />
        </Card>
      ) : null}
    </Box>
  );
});

function KpiTile({ item, metricsStatus, derivedStatus, derivedFailedSource, compareStatus, compareLabel, onRetry }: KpiStripProps & { item: KpiView }) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const label = t(`results.report.kpis.${item.id}.label`);
  const status = item.source === 'metrics' ? metricsStatus : derivedStatus;
  const testId = `sim-results.report.kpis.${item.id}`;

  if (status === 'error' && derivedFailedSource) {
    return (
      <Card data-testid={testId}>
        <ErrorState title={t(`results.report.errors.${derivedFailedSource}`)} onRetry={() => onRetry(derivedFailedSource)} retryLabel={t('results.report.retry')} />
      </Card>
    );
  }
  if (status !== 'ready') {
    return (
      <Card data-testid={testId} aria-busy="true">
        <Box sx={{ display: 'grid', gap: opsSpace[2] }}>
          <span>{label}</span>
          <Skeleton {...VALUE_SKELETON} aria-label={t('results.report.loading')} />
          <Skeleton {...LINE_SKELETON} />
        </Box>
      </Card>
    );
  }

  const sub = item.sub ? t(`results.report.kpis.${item.id}.sub`, formatValues(item.sub, locale)) : undefined;
  const comparable = item.source === 'metrics' && compareLabel != null;
  return (
    <Box data-testid={testId} sx={{ display: 'grid' }}>
      <KpiCard
        label={label}
        value={item.value == null ? t('results.report.noValue') : formatNumber(item.value, locale, item.digits)}
        unit={item.value == null || !item.unitKey ? undefined : t(item.unitKey)}
        sub={sub}
      >
        {comparable && compareStatus === 'loading' ? <Skeleton {...DELTA_SKELETON} aria-label={t('results.report.loading')} /> : null}
        {comparable && item.delta ? (
          <Box component="span" sx={{ fontSize: opsFontSize.xs, color: opsColors.neutral.inkMute }}>
            <Box
              component="span"
              data-tone={item.delta.tone}
              sx={{ fontFamily: opsFontFamily.mono, fontWeight: opsFontWeight.semibold, color: TONE_COLOR[item.delta.tone] }}
            >
              {item.delta.delta === 0 ? t('results.report.kpis.same') : formatSignedNumber(item.delta.delta, locale, item.digits)}
            </Box>{' '}
            {t('results.report.kpis.versus', { run: compareLabel })}
          </Box>
        ) : null}
      </KpiCard>
    </Box>
  );
}

function formatValues(values: Readonly<Record<string, number>>, locale: string): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, formatNumber(value, locale, Number.isInteger(value) ? 0 : 1)])
  );
}
